import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import { Pressable } from 'native-base';
import React from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import { RiskRating } from '../../../../../../../../../components/RiskRating';
import { TableContent } from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useDirectTreasuryAssetContext } from '../../../../../contexts/products/DirectTreasury';
import { useDirectTreasuryAssetController } from '../../../../../hooks/products/DirectTreasury';
import { LateralBar } from '../../../components/LateralBar';
import { ListLoadingItemTablet } from '../ListLoadingItem/tablet';

import { DirectTreasuryListItemTypes } from '.';

export const DirectTreasuryListItemTablet: React.FC<DirectTreasuryListItemTypes.Props> = ({
  offer,
}) => {
  const id = offer.productId;
  const {
    control,
    amountItem,
    currentValue,
    currentValueFormatted,
    handleChangeAmountItem,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    helpTextProps,
    isFocused,
    showMessageAdjustUnitPrice,
  } = useDirectTreasuryAssetController(offer);
  const { setModalOpen, setOfferSelected } = useDirectTreasuryAssetContext();
  const { infoGetter, selectedTab } = useGlobalAssetContext();

  const completeOffer = { ...offer, orderQuantity: amountItem || 0, orderValue: currentValue || 0 };

  const { Button, isSelected, forceLoading } = infoGetter(
    'directTreasury',
    completeOffer,
    'productId'
  );

  const onOpenDetailsModal = () => {
    setModalOpen(true);
    setOfferSelected(offer);
  };

  const middleInfos = [
    {
      label: 'Rentabilidade',
      value: <UI.Text color="neutral.dark">{offer.profitabilityNormalized}</UI.Text>,
    },
    {
      label: 'Risco',
      value: (
        <RiskRating
          currentRiskRating={Number(offer.riskRatingLevelNormalized) || 0}
          tooltip={offer.riskRatingNormalized}
        />
      ),
    },
    {
      label: 'Vencimento',
      value: <UI.Text color="neutral.dark">{offer.maturityDateFormattedNormalized}</UI.Text>,
    },
    {
      label: 'Com juros',
      value: <UI.Text color="neutral.dark">{offer.feesPaymentNormalized}</UI.Text>,
    },
  ];

  if (forceLoading) {
    return <ListLoadingItemTablet />;
  }

  return (
    <TableContent
      direction="row"
      space={0}
      p={0}
      px={Spacing['inline.xxxs']}
      isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <VStack padding={Spacing['inset.sm']} space={Spacing['stack.xxxs']} w="full">
        <HStack justifyContent="space-between" alignItems="center">
          <UI.Text variant="semibold" color="neutral.darkest">
            {offer.productNameNormalized}
          </UI.Text>
          <Pressable onPress={onOpenDetailsModal}>
            <UI.Icon name="Info" size="xs" />
          </Pressable>
        </HStack>
        <HStack justifyContent="space-between">
          {middleInfos.map(({ label, value }) => (
            <VStack space={Spacing['stack.quarck']} key={label}>
              <UI.Text color="neutral.black" variant="semibold">
                {label}
              </UI.Text>
              {value}
            </VStack>
          ))}
        </HStack>
        <HStack space={Spacing['inline.xxxs']} justifyContent="space-between" w="full">
          <VStack space={Spacing['stack.quarck']} w="25%">
            <UI.Text color="neutral.black" variant="semibold">
              Quantidade
            </UI.Text>
            <UI.InputStepper
              stepperValue={0.01}
              max={1000000}
              min={offer.minimumStepQuantity || 0}
              name="treasury-amount-order-input"
              testId="treasury-amount-order-input"
              keyboardType="numeric"
              fontSize={Typography['fontsize.md']}
              defaultValue={amountItem || 0}
              onChange={handleChangeAmountItem}
              showErrorMessage={false}
              disabled={isSelected}
            />
          </VStack>
          <VStack space={Spacing['stack.quarck']} w="60%">
            <UI.Text color="neutral.black" variant="semibold">
              A partir de:
            </UI.Text>
            <InputMoney
              id={`directTreasury-input${id}`}
              name="priceItem"
              control={control}
              handleFocus={handleInputFocus}
              handleBlur={handleInputBlur}
              handleSubmit={handleSubmit}
              handleClear={handleInputClear}
              isDisabled={isSelected}
              currentValue={currentValueFormatted}
              hasFocus={isFocused}
              minValue={offer.minimumApplication || 0}
              customInputProps={{
                ...{
                  ...(showMessageAdjustUnitPrice && {
                    helpTextProps,
                    helpText,
                  }),
                },
                ...{ ...(isFocused && hasError && { error: hasError }) },
                ...{ ...(isFocused && !hasError && { helpText }) },
              }}
            />
          </VStack>
          <Stack alignSelf="flex-end">
            <Button
              id={selectedTab.typeGetterId}
              offer={completeOffer}
              productContextConstant={selectedTab}
            />
          </Stack>
        </HStack>
      </VStack>
    </TableContent>
  );
};
